import { useEffect, useState } from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Form, LoadingOverlay } from "@covertree/ui";

import { FormProvider, useForm } from "react-hook-form";
import { CoverageFilesFormData } from "@forms";

import { AddCoverageDetails } from "@graphql/api";

import {
  addedCoverageRedirectPath,
  isCoverageDocument,
  moveBackInPathname,
} from "@utils";

import useAppDispatch from "@redux/hooks/useAppDispatch";
import useAppSelector from "@redux/hooks/useAppSelector";
import { useUploadCoverageDocumentsMutation } from "@redux/documents/documentsRestApi";
import {
  saveExternalPolicyDocuments,
  selectAddedCoverageDetails,
  selectDocumentsUploadStatus,
  selectExternalPolicyDocuments,
} from "@redux/coverages/coveragesSlice";

import ExternalCoverageDocumentsContent from "./ExternalCoverageDocumentsContent";
import {
  BASE_COVERAGE_DATA_PATH,
  COVERAGE_DATA_ROUTES,
} from "@routing/Residents/ProofOfCoverage/CoverageData";
import { ADD_EVENT_ROUTES } from "@routing/Residents/ProofOfCoverage";

interface ExternalCoverageDocumentsProps {
  isAddingEvent: boolean;
}

export function ExternalCoverageDocuments({
  isAddingEvent,
}: ExternalCoverageDocumentsProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { residentId } = useParams();

  const savedFiles = useAppSelector(selectExternalPolicyDocuments);
  const {
    hasDocumentsUploadFailed: isFailed,
    addedCoverageId: externalCoverageId,
  } = useAppSelector(selectDocumentsUploadStatus);
  const {
    updatedResident: resident,
    alreadyAddedCoverageId,
    alreadyAddedCoverageStatus,
    residentStatusChanged,
  } = useAppSelector(selectAddedCoverageDetails);

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);

  const form = useForm<CoverageFilesFormData>({
    defaultValues: { files: savedFiles },
  });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    reset();
  }, [savedFiles]);

  const [uploadCoverageDocuments] = useUploadCoverageDocumentsMutation();

  const uploadDocuments = async (files: File[]) => {
    if (isAddingEvent && files.length === 0) {
      setIsUploadSuccess(true);
      setIsUploadLoading(false);
      return;
    }

    const results = await Promise.all(
      files.map((file) =>
        uploadCoverageDocuments({
          residentId: residentId,
          coverageId: externalCoverageId,
          file,
        })
      )
    );

    const failedFiles = files.filter((file) => {
      const result = results.find((res) => res?.["data"]?.name === file.name);
      return !isCoverageDocument(result?.["data"]);
    });

    if (!!failedFiles.length) {
      dispatch(
        saveExternalPolicyDocuments({ files: failedFiles, isFailed: true })
      );
    } else {
      setIsUploadSuccess(true);
    }

    setIsUploadLoading(false);
  };

  const submit = async ({ files }: CoverageFilesFormData): Promise<void> => {
    setIsUploadLoading(true);
    if (isFailed) {
      await uploadDocuments(files);
    } else {
      if (isAddingEvent) {
        navigate({
          pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_SUMMARY}`,
          search: createSearchParams(searchParams).toString(),
        });
        dispatch(
          saveExternalPolicyDocuments({ files: files, isFailed: false })
        );
        return;
      }
      navigate({
        pathname: `${moveBackInPathname(location.pathname, 1)}/${BASE_COVERAGE_DATA_PATH}/${COVERAGE_DATA_ROUTES.COVERAGE_DETAILS}`,
        search: createSearchParams(searchParams).toString(),
      });
      dispatch(saveExternalPolicyDocuments({ files: files, isFailed: false }));
    }
  };

  if (isUploadLoading) return <LoadingOverlay />;

  if (isUploadSuccess) {
    if (isAddingEvent) {
      navigate({
        pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_ADDED}`,
        search: createSearchParams(searchParams).toString(),
      });
    } else {
      navigate(
        addedCoverageRedirectPath(searchParams, location.pathname, {
          resident,
          alreadyAddedCoverageId,
          alreadyAddedCoverageStatus,
          residentStatusChanged,
        } as unknown as AddCoverageDetails),
        { replace: true }
      );
    }

    return <LoadingOverlay />;
  }

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <FormProvider {...form}>
        <ExternalCoverageDocumentsContent
          isAddingEvent={isAddingEvent}
          isFailed={isFailed}
        />
      </FormProvider>
    </Form>
  );
}
export default ExternalCoverageDocuments;
